/* The three shapes of exercise: pick one option, sort items into bins, put a
 * flow back in order.
 *
 * Two rules are load-bearing. The carry item — one extra scenario from a
 * prerequisite chapter, graded with this chapter's own categories — is merged
 * into the item set before anything is rendered, so grading and rendering can
 * never disagree about what the exercise contains. And the presentation order
 * is re-rolled per attempt, rejecting any permutation whose answer key can be
 * read off the sequence.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { columnOrder, exOf, itemOrder, optionOrder } from '../../engine/exercises';
import { useApp } from '../../state/AppProvider';
import type { Block, NodeDef } from '../../types';

interface ExState {
  /** classify: the item picked and waiting for a bin */
  pick: number | null;
  /** classify: item index → bin */
  placed: Record<number, number>;
  /** classify: the order items were placed, so bins keep the learner's order */
  seq: number[];
  /** order: the sequence chosen so far */
  order: number[];
  /** choice: the option that was answered correctly, -1 while unanswered */
  answered: number;
  /** choice: the wrong options already tried */
  picks: number[];
  misses: number;
  msg: string | null;
  tone: 'right' | 'wrong' | null;
}

const INITIAL: ExState = {
  pick: null,
  placed: {},
  seq: [],
  order: [],
  answered: -1,
  picks: [],
  misses: 0,
  msg: null,
  tone: null,
};

export function CheckExercise({ node, block, round }: { node: NodeDef; block: Block; round: number }) {
  const { t } = useTranslation();
  const { progress, passCheck, missCheck, replayCheck, announce } = useApp();
  const [state, setState] = useState<ExState>(INITIAL);

  const ex = exOf(block);
  const seed = `${block.id}|${round}`;

  /* Presentation orders, per attempt. */
  const choiceOrder = useMemo(
    () => (ex?.type === 'choice' ? optionOrder(ex.options, `${seed}|o`) : []),
    [ex, seed],
  );
  const poolOrder = useMemo(
    () => (ex?.type === 'classify' ? itemOrder(ex.items, ex.binCount, `${seed}|i`) : []),
    [ex, seed],
  );
  const binOrder = useMemo(
    () => (ex?.type === 'classify' ? columnOrder(ex.binCount, `${seed}|b`, true) : []),
    [ex, seed],
  );
  const sequenceOrder = useMemo(
    () => (ex?.type === 'order' ? columnOrder(ex.items.length, `${seed}|s`, true) : []),
    [ex, seed],
  );

  if (!ex) return null;

  const passed = !!progress.chk[block.id];
  const instruction =
    ex.type === 'classify' ? t('pickItem') : ex.type === 'order' ? t('pickOrder') : '';

  const wrong = (message: string, resetPick = false) => {
    setState((s) => ({
      ...s,
      misses: s.misses + 1,
      msg: message,
      tone: 'wrong',
      ...(resetPick ? { pick: null } : {}),
    }));
    missCheck(node.id);
    announce(message);
  };

  const answerChoice = (index: number) => {
    if (ex.type !== 'choice') return;
    const option = ex.options[index];
    if (state.answered > -1 || state.picks.includes(index)) return;
    if (option.ok) {
      setState((s) => ({ ...s, answered: index, msg: t(option.fbKey), tone: 'right' }));
      passCheck(node, block);
    } else {
      wrong(`${t('wrong')} ${t(option.fbKey)} ${t('tryOther')}`);
    }
  };

  const pickItem = (index: number) => {
    setState((s) => ({ ...s, pick: index, msg: null, tone: null }));
  };

  const placeInBin = (bin: number) => {
    if (ex.type !== 'classify') return;
    if (state.pick === null) {
      setState((s) => ({ ...s, msg: t('pickItem'), tone: null }));
      return;
    }
    const item = ex.items[state.pick];
    if (item.bin === bin) {
      const placed = { ...state.placed, [state.pick]: bin };
      const seq = [...state.seq, state.pick];
      const complete = Object.keys(placed).length === ex.items.length;
      setState((s) => ({
        ...s,
        placed,
        seq,
        pick: null,
        msg: `${t('right')} ${t(item.whyKey)}`,
        tone: 'right',
      }));
      if (complete) passCheck(node, block);
    } else {
      wrong(`${t('wrong')} ${t(item.whyKey)}`, true);
    }
  };

  const addToSequence = (index: number) => {
    if (ex.type !== 'order') return;
    const item = ex.items[index];
    if (index === state.order.length) {
      const order = [...state.order, index];
      setState((s) => ({ ...s, order, msg: `${t('right')} ${t(item.whyKey)}`, tone: 'right' }));
      if (order.length === ex.items.length) passCheck(node, block);
    } else {
      wrong(`${t('notNow')} ${t(item.whyKey)}`);
    }
  };

  return (
    <div className="ex">
      {instruction && <p className="ex-inst">{instruction}</p>}
      {state.msg && <p className="ex-fb" data-tone={state.tone ?? undefined}>{state.msg}</p>}

      {ex.type === 'choice' && (
        <div className="ex-pool">
          {choiceOrder.map((index) => {
            const option = ex.options[index];
            const tried = state.picks.includes(index);
            const result = state.answered > -1 && option.ok ? 'right' : tried ? 'wrong' : undefined;
            return (
              <button
                key={index}
                type="button"
                className="ex-opt"
                data-act="choice"
                data-id={block.id}
                data-i={index}
                data-result={result}
                disabled={state.answered > -1 || tried}
                onClick={() => answerChoice(index)}
              >
                <span className="mark">{result === 'right' ? '✓' : result === 'wrong' ? '✕' : '·'}</span>
                <span>
                  <CarryChip chapter={option.carry} />
                  {t(option.tKey)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {ex.type === 'classify' && (
        <>
          <div className="ex-pool">
            {poolOrder.map((index) => {
              if (state.placed[index] !== undefined) return null;
              const item = ex.items[index];
              const picked = state.pick === index;
              return (
                <button
                  key={index}
                  type="button"
                  className="ex-opt"
                  data-act="pick"
                  data-id={block.id}
                  data-i={index}
                  data-pick={picked}
                  aria-pressed={picked}
                  onClick={() => pickItem(index)}
                >
                  <span className="mark">{picked ? '→' : '·'}</span>
                  <span>
                    <CarryChip chapter={item.carry} />
                    {t(item.tKey)}
                  </span>
                </button>
              );
            })}
            {Object.keys(state.placed).length === ex.items.length && (
              <p className="meta">
                ✓ {ex.items.length} / {ex.items.length}
              </p>
            )}
          </div>
          <div className="ex-bins" style={{ gridTemplateColumns: `repeat(${Math.min(ex.binCount, 2)},1fr)` }}>
            {binOrder.map((bin) => {
              const labels = t(ex.binsKey, { returnObjects: true }) as string[];
              const inBin = state.seq.filter((itemIndex) => state.placed[itemIndex] === bin);
              return (
                <button
                  key={bin}
                  type="button"
                  className="ex-bin"
                  data-act="bin"
                  data-id={block.id}
                  data-b={bin}
                  onClick={() => placeInBin(bin)}
                >
                  <span className="lbl">{labels[bin]}</span>
                  {inBin.map((itemIndex) => (
                    <span key={itemIndex} className="slot">
                      {t(ex.items[itemIndex].tKey)}
                    </span>
                  ))}
                </button>
              );
            })}
          </div>
        </>
      )}

      {ex.type === 'order' && (
        <div className="ex-pool">
          {state.order.map((index, position) => (
            <div key={index} className="ex-opt" data-result="right">
              <span className="mark num">{position + 1}</span>
              <span>{t(ex.items[index].tKey)}</span>
            </div>
          ))}
          {sequenceOrder.map((index) => {
            if (state.order.includes(index)) return null;
            return (
              <button
                key={index}
                type="button"
                className="ex-opt"
                data-act="seq"
                data-id={block.id}
                data-i={index}
                onClick={() => addToSequence(index)}
              >
                <span className="mark">·</span>
                <span>{t(ex.items[index].tKey)}</span>
              </button>
            );
          })}
        </div>
      )}

      {passed && (
        <div className="ex-passed">
          <span>
            ✓ {t('passed')} · {block.xp} {t('xp')}
          </span>
          <span className="meta num">
            {state.misses ? `${state.misses} ${t('misses')}` : t('firstTry')}
          </span>
          <button type="button" className="btn btn-ghost btn-sm" data-act="replay" onClick={() => replayCheck(block.id)}>
            {t('replay')}
          </button>
        </div>
      )}
    </div>
  );
}

/* An item carried over from a prerequisite chapter says so, because the point
   of it is transfer, not recall of this chapter's slogan. */
function CarryChip({ chapter }: { chapter?: string }) {
  const { t } = useTranslation();
  if (!chapter) return null;
  return (
    <span className="rc">
      {t('recallChip')} {chapter}
    </span>
  );
}
